/**
 * Mermaid runs every node/edge label through `marked.lexer` (it treats
 * labels as markdown). Before lexing it rewrites `<br/>` into newlines, so a
 * label like `"1. Investigate<br/>uplift"` becomes `1. Investigate\nuplift` —
 * which `marked` parses as an ordered *list*. Mermaid has no renderer for list
 * nodes, so it emits the literal text `Unsupported markdown: list` instead of
 * the label (or silently drops it in the non-HTML path).
 *
 * Additionally, `&` is the AND-concatenation operator in the flowchart grammar
 * and can break parsing even inside quoted labels.
 *
 * `sanitizeMermaidLabels` rewrites the *inside* of double-quoted label segments
 * so that neither of these confuses mermaid, while leaving diagram syntax
 * (arrows, node ids, directives) untouched.
 */

// Escape a markdown list marker at the start of a line/chunk so `marked` no
// longer recognises it as a list item. Ordered markers (`1. ` and `1) `) get
// the delimiter escaped; unordered markers (`- `, `* `, `+ `) get the bullet
// escaped.
const escapeListMarkers = (label: string): string =>
  label
    // `<br/>`-delimited chunks: escape a marker that follows a line break.
    .replace(/(<br\s*\/?>)(\s*)(\d+)([.)])(\s)/gi, '$1$2$3\\$4$5')
    .replace(/(<br\s*\/?>)(\s*)([-*+])(\s)/gi, '$1$2\\$3$4')
    // Start of the whole label.
    .replace(/^(\s*)(\d+)([.)])(\s)/, '$1$2\\$3$4')
    .replace(/^(\s*)([-*+])(\s)/, '$1\\$2$3');

// `&` is significant in the flowchart grammar; neutralise it inside labels.
const escapeAmpersand = (label: string): string =>
  label.replace(/&(?!(?:amp|lt|gt|quot|#\d+|#x[0-9a-f]+);)/gi, '&amp;');

const sanitizeLabel = (label: string): string =>
  escapeAmpersand(escapeListMarkers(label));

/**
 * Escapes markdown list markers and `&` inside double-quoted mermaid labels so
 * mermaid renders the label text verbatim instead of failing with
 * `Unsupported markdown: list`. Content outside quotes is left unchanged.
 */
export const sanitizeMermaidLabels = (code: string): string =>
  code.replace(
    /"([^"]*)"/g,
    (_match, label: string) => `"${sanitizeLabel(label)}"`
  );
